import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { useGetStudentsByCourseIdQuery, useGetPostsByThreadQuery } from "../../../graphql/generated/graphql";

import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { Users } from "@phosphor-icons/react/dist/ssr/Users";

interface ParticipationOverviewProps {
  threadId?: string;
  courseId?: string;
}

interface Student {
  id: string;
  name: string;
  email: string;
  initials: string;
  hasParticipated: boolean;
  postCount: number;
}

const ParticipationOverview: React.FC<ParticipationOverviewProps> = ({ threadId, courseId }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"participated" | "not_participated">("participated");
  const [students, setStudents] = useState<Student[]>([]);

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetStudentsByCourseIdQuery({
    variables: {
      courseId: courseId || "",
      searchQuery: "",
      page: 1,
      pageSize: 100,
      sortBy: "name_asc",
    },
    skip: !courseId,
  });

  const {
    data: postsData,
    loading: postsLoading,
    error: postsError,
  } = useGetPostsByThreadQuery({
    variables: {
      threadId: threadId || "",
      page: 1,
      pageSize: 200,
    },
    skip: !threadId,
  });

  useEffect(() => {
    if (studentsData?.getStudentsByCourseId?.data && postsData?.getPostsByThread?.data) {
      const enrolledStudents = studentsData.getStudentsByCourseId.data;
      const threadPosts = postsData.getPostsByThread.data;

      // Count posts by student for this thread
      const studentPostCounts = new Map<string, number>();
      threadPosts.forEach((post) => {
        if (post.author?.id) {
          const currentCount = studentPostCounts.get(post.author.id) || 0;
          studentPostCounts.set(post.author.id, currentCount + 1);
        }
      });

      // Transform enrolled students into participation data
      const transformedStudents: Student[] = enrolledStudents.map((student) => {
        const postCount = studentPostCounts.get(student.id) || 0;
        const initials =
          student.name
            ?.split(" ")
            .map((name) => name.charAt(0))
            .join("")
            .toUpperCase()
            .slice(0, 2) || "??";

        return {
          id: student.id,
          name: student.name || "Unknown Student",
          email: student.email || "",
          initials,
          hasParticipated: postCount > 0,
          postCount,
        };
      });

      setStudents(transformedStudents);
    } else if (studentsData?.getStudentsByCourseId?.data && !threadId) {
      const enrolledStudents = studentsData.getStudentsByCourseId.data;
      const transformedStudents: Student[] = enrolledStudents.map((student) => {
        const initials =
          student.name
            ?.split(" ")
            .map((name) => name.charAt(0))
            .join("")
            .toUpperCase()
            .slice(0, 2) || "??";

        return {
          id: student.id,
          name: student.name || "Unknown Student",
          email: student.email || "",
          initials,
          hasParticipated: false,
          postCount: 0,
        };
      });

      setStudents(transformedStudents);
    }
  }, [studentsData, postsData, threadId]);

  if (studentsLoading || postsLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <CustomText
            text="Participation Overview"
            size="m"
            weight="bold"
            style={styles.sectionTitle}
            useTranslationText={false}
          />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="Loading participation data..." size="m" useTranslationText={false} />
      </View>
    );
  }

  if (studentsError || postsError) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <CustomText
            text="Participation Overview"
            size="m"
            weight="bold"
            style={styles.sectionTitle}
            useTranslationText={false}
          />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="Error loading participation data. Please try again." size="m" useTranslationText={false} />
      </View>
    );
  }

  if (!courseId) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <CustomText
            text="Participation Overview"
            size="m"
            weight="bold"
            style={styles.sectionTitle}
            useTranslationText={false}
          />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="Course information not available." size="m" useTranslationText={false} />
      </View>
    );
  }

  // Calculate statistics
  const participatedStudents = students.filter((student) => student.hasParticipated);
  const notParticipatedStudents = students.filter((student) => !student.hasParticipated);
  const totalStudents = students.length;
  const participationRate = totalStudents > 0 ? Math.round((participatedStudents.length / totalStudents) * 100) : 0;

  const handleToggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const renderStatCard = (number: number | string, label: string, isLarge?: boolean) => (
    <View style={[styles.statCard, isLarge && styles.largeStatCard]}>
      <CustomText
        text={number.toString()}
        size={isLarge ? "xxl" : "xl"}
        weight="ultraBold"
        style={styles.statNumber}
        useTranslationText={false}
      />
      <CustomText text={label} size="xs" weight="medium" style={styles.statLabel} useTranslationText={false} />
    </View>
  );

  const renderStudentAvatar = (student: Student) => (
    <View key={student.id} style={styles.studentRow}>
      <View style={styles.avatarContainer}>
        <View style={styles.avatar}>
          <CustomText
            text={student.initials}
            size="s"
            weight="bold"
            style={styles.avatarText}
            useTranslationText={false}
          />
        </View>
        <View style={styles.studentInfo}>
          <CustomText
            text={student.name}
            size="m"
            weight="medium"
            style={styles.studentName}
            useTranslationText={false}
          />
          <CustomText text={student.email} size="s" style={styles.studentEmail} useTranslationText={false} />
        </View>
      </View>
      <View style={styles.statusContainer}>
        <CustomText
          text={student.hasParticipated ? "PARTICIPATED" : "NOT PARTICIPATED"}
          size="xs"
          weight="bold"
          style={[styles.statusText, student.hasParticipated ? styles.participatedText : styles.notParticipatedText]}
          useTranslationText={false}
        />
        {student.hasParticipated && student.postCount > 0 && (
          <CustomText
            text={`${student.postCount} post${student.postCount === 1 ? "" : "s"}`}
            size="xs"
            style={styles.postCountText}
            useTranslationText={false}
          />
        )}
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <CustomText
          text="Participation Overview"
          size="m"
          weight="bold"
          style={styles.sectionTitle}
          useTranslationText={false}
        />
        <CustomButton
          text={showDetails ? "Hide Details" : "Show Details"}
          size="s"
          bold={true}
          styleType="lightStroke"
          onPress={handleToggleDetails}
          useTranslationText={false}
          leftIcon={<Users size={20} color="#3E68FE" weight="bold" style={{ marginRight: 0 }} />}
          textStyle={{ color: "#3E68FE" }}
        />
      </View>

      <CustomSpacing type="vertical" size="m" />

      {/* Stats Grid */}
      <View style={styles.statsGrid}>
        {renderStatCard(participatedStudents.length, "PARTICIPATED")}
        {renderStatCard(notParticipatedStudents.length, "NOT PARTICIPATED")}
        {renderStatCard(`${participationRate}%`, "PARTICIPATION RATE", true)}
      </View>

      {/* Details Section */}
      {showDetails && (
        <>
          <CustomSpacing type="vertical" size="m" />

          {/* Tab Buttons */}
          <View style={styles.tabContainer}>
            <CustomButton
              text={`${participatedStudents.length} Participated`}
              size="m"
              styleType={selectedTab === "participated" ? "primary" : "lightStroke"}
              onPress={() => setSelectedTab("participated")}
              useTranslationText={false}
              style={styles.tabButton}
            />
            <CustomButton
              text={`${notParticipatedStudents.length} Not Participated`}
              size="m"
              styleType={selectedTab === "not_participated" ? "primary" : "lightStroke"}
              onPress={() => setSelectedTab("not_participated")}
              useTranslationText={false}
              style={styles.tabButton}
            />
          </View>

          <CustomSpacing type="vertical" size="s" />

          {/* Student List */}
          <ScrollView style={styles.studentList} showsVerticalScrollIndicator={false}>
            {(selectedTab === "participated" ? participatedStudents : notParticipatedStudents).map(renderStudentAvatar)}
          </ScrollView>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    color: "#101828",
  },
  statsGrid: {
    flexDirection: "row",
    gap: 16,
  },
  statCard: {
    flex: 1,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 80,
  },
  largeStatCard: {
    backgroundColor: "#F0F9FF",
    borderColor: "#C7D2FE",
  },
  statNumber: {
    color: "#101828",
    textAlign: "center",
  },
  statLabel: {
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    textAlign: "center",
    marginTop: 4,
  },
  tabContainer: {
    flexDirection: "row",
    gap: 8,
  },
  tabButton: {
    flex: 1,
  },
  studentList: {
    maxHeight: 300,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  studentRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  avatarContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  avatarText: {
    color: "#FFFFFF",
  },
  studentInfo: {
    flex: 1,
  },
  studentName: {
    color: "#101828",
  },
  studentEmail: {
    color: "#6B7280",
    marginTop: 2,
  },
  statusContainer: {
    alignItems: "flex-end",
  },
  statusText: {
    letterSpacing: 0.5,
  },
  participatedText: {
    color: "#059669",
  },
  notParticipatedText: {
    color: "#6B7280",
  },
  postCountText: {
    color: "#6B7280",
    marginTop: 2,
  },
});

export default ParticipationOverview;
